/**
 * Email validation utilities for VerifyPDF
 * 
 * This module contains:
 * - Email format validation
 * - Email alias cleaning
 * - Business email domain checking
 */

/**
 * Validates email format with comprehensive checks
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if email format is valid
 */
export const isValidEmailFormat = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  // Trim whitespace
  email = email.trim();
  
  // Basic format check
  const basicFormatRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  if (!basicFormatRegex.test(email)) return false;

  // Additional validations
  const [localPart, domain] = email.split('@');

  // Local part checks
  if (localPart.length > 64) return false;
  if (/^[.-]|[.-]$/.test(localPart)) return false; // Can't start/end with dots or hyphens
  if (/(\.{2,}|-{2,})/.test(localPart)) return false; // No consecutive dots or hyphens

  // Domain checks
  if (domain.length > 255) return false;
  if (!/^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/.test(domain)) return false;
  if (domain.split('.').some(part => part.length > 63)) return false;
  
  // TLD checks (must be at least 2 chars and only letters)
  const tld = domain.split('.').pop();
  if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) return false;

  return true;
};

/**
 * Removes email aliases (everything after + in local part)
 * @param {string} email - Email address with potential alias
 * @returns {string} - Cleaned email address
 */
export const cleanEmailAlias = (email) => {
  const [localPart, domain] = email.split('@');
  const cleanedLocalPart = localPart.split('+')[0];
  return `${cleanedLocalPart}@${domain}`;
};

/**
 * Checks if an email domain is considered personal/consumer
 * @param {string} email - Email address to check
 * @param {string[]} personalDomains - Array of personal email domains
 * @returns {boolean} - True if domain is personal
 */
export const isPersonalEmail = (email, personalDomains) => {
  if (!email || !personalDomains) return false;
  
  const cleanedEmail = cleanEmailAlias(email);
  const domain = cleanedEmail.split('@')[1]?.toLowerCase();
  
  return domain && personalDomains.includes(domain);
};